import React, { useMemo } from "react";
import { type GameConfig } from "kadow-jetpack-model";
import KC_Card from "../../../../designsystem/KC_Card/KC_Card";
import { useDispatch } from "react-redux";
import {
  updateJetpackConfigThunk,
  useJetpackConfigSelector
} from "../../../../redux/jetpackConfig";
import { useAccountToDisplaySelector } from "../../../../redux/actions/user";
import GameCardPreview from "../../../23_GameCardPreview/GameCardPreview";
import { GameThumbnailDTO } from "../../../../model/GameDTO";
import { DEFAULT_EVENTS } from "../../../../utils/constants";
import { buildGameThumbnailDTO } from "../../../../utils/helpers/buildGameThumbnailDTO";
import { ActiveEvent } from "kadow-jetpack-model/src";
import { useEventsSelector } from "../../../../redux/actions/wheel";
import { tt } from "../../../../utils/i18n/i18n";

import { getDisplayNameForGameType } from "../../../../utils/helpers/getDisplayNameForGameType";
import KC_Text from "../../../../designsystem/typography/KC_Text/KC_Text";
import Dot from "../../../../components/Dot/Dot";
import { useSubscriptionPlansSelector } from "../../../../redux/actions/app";

interface GameSectionProps {
  displayGame: { games: GameConfig[]; activeEvents: ActiveEvent[] };
}

export default function GameSection(props: GameSectionProps) {
  const dispatch = useDispatch();
  const { accountToDisplay } = useAccountToDisplaySelector();
  const events = useEventsSelector();
  const config = useJetpackConfigSelector();

  const handleDelete = (gameName: string) => {
    if (!config) {
      return;
    }

    if (!confirm(tt("confirm_deletion"))) {
      return;
    }

    const newGamesList = props.displayGame.games?.filter(
      (game) => game.name !== gameName
    );

    dispatch(
      updateJetpackConfigThunk(
        { games: newGamesList },
        accountToDisplay?.trackingID,
        accountToDisplay?.token
      )
    );
  };

  const handleDeleteEvent = (ae: ActiveEvent) => {
    if (!config) {
      return;
    }

    if (!confirm(tt("confirm_deletion"))) {
      return;
    }

    const idx =
      config?.activeEvents?.findIndex(
        (configAE) =>
          configAE.id === ae.id &&
          configAE.sts === ae.sts &&
          configAE.ets === ae.ets &&
          configAE.gameType === ae.gameType
      ) ?? -1;

    if (idx === -1) {
      return;
    }

    const newActiveEvents = [...(config.activeEvents || [])];
    newActiveEvents.splice(idx, 1);

    dispatch(
      updateJetpackConfigThunk(
        { activeEvents: newActiveEvents },
        accountToDisplay?.trackingID,
        accountToDisplay?.token
      )
    );
  };

  const plans = useSubscriptionPlansSelector();

  const plan = useMemo(() => {
    return plans?.find((p) => p._id === accountToDisplay?.saasPlanId) ?? null;
  }, [accountToDisplay]);

  const canDelete = useMemo(() => {
    if (!plan?.price) {
      return false;
    }
    return (
      plan.price > 0 &&
      (!!props.displayGame?.activeEvents?.length ||
        !!props.displayGame?.games?.length)
    );
  }, [props.displayGame.activeEvents, props.displayGame.games, plan]);

  const activeEvents = useMemo<React.ReactNode[]>(() => {
    if (!props.displayGame.activeEvents?.length) {
      return [];
    }

    return props.displayGame.activeEvents.map((ae, index) => {
      if (!ae.gameType) {
        return null;
      }

      const eventConfig = events?.find((e) => e._id === ae.id);

      if (!eventConfig) {
        return null;
      }

      const skin = eventConfig.skins.find((s) => s.game === ae.gameType);

      if (!skin) {
        return null;
      }

      return (
        <KC_Card
          key={`${ae.id}-${index}`}
          title={getDisplayNameForGameType(ae.gameType)}
          forceShowHeader={true}
          boxShadow={false}
          description={""}
          useTwicPics={true}
          isGameCardPreview={true}
          isSelected={true}
          className={"border"}
          canDelete={canDelete}
          handleDelete={() => handleDeleteEvent(ae)}
          children={
            <GameCardPreview
              gameCardConfig={skin.gameCardConfig}
              eventId={ae.id}
            />
          }
        />
      );
    });
  }, [props.displayGame.activeEvents, events, canDelete]);

  const games = useMemo<React.ReactNode[]>(() => {
    if (!props.displayGame.games?.length) {
      return [];
    }

    const res: GameThumbnailDTO[] = [];

    props.displayGame.games?.map((g) => {
      buildGameThumbnailDTO(res, DEFAULT_EVENTS, "default", g.name);
    });

    return res.map((game) => {
      return (
        <KC_Card
          key={game.name as string}
          title={getDisplayNameForGameType(game.name)}
          boxShadow={false}
          useTwicPics={true}
          isGameCardPreview={true}
          forceShowHeader={true}
          className="border"
          onClick={() => {}}
          canDelete={canDelete}
          isSelected={true}
          handleDelete={() => {
            handleDelete(game.name as string);
          }}
          children={
            <>
              <GameCardPreview
                gameCardConfig={game.config}
                eventId={game.eventId}
              />
              <div className="d-flex align-items-center">
                <Dot color="green" />
                <KC_Text
                  text={tt("active_permanently")}
                  size="md"
                  weight="reg"
                  className="ml-2"
                />
              </div>
            </>
          }
        />
      );
    });
  }, [props.displayGame.games, canDelete]);

  const GAMES = useMemo(() => {
    return [...(games || []), ...(activeEvents || [])];
  }, [games, activeEvents]);

  if (!GAMES.length) {
    return null;
  }

  return (
    <div className="d-flex flex-column st-gap">{GAMES.map((game) => game)}</div>
  );
}
